import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Briefcase, Search } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import { JOB_STATUSES, StatusBadge, money, fmtDate } from "@/lib/format";
import MobileListCard from "@/components/MobileListCard";

export default function Jobs() {
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const initial = params.get("status") || "";
    const [list, setList] = useState(null);
    const [err, setErr] = useState("");
    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState(initial);

    const fetchList = async () => {
        setErr("");
        try {
            const { data } = await api.get("/jobs", {
                params: {
                    ...(statusFilter ? { status_filter: statusFilter } : {}),
                    ...(q.trim() ? { q: q.trim() } : {}),
                },
            });
            setList(data.jobs);
        } catch (e) {
            setErr(apiErrorMessage(e));
        }
    };

    useEffect(() => {
        fetchList();
        const next = new URLSearchParams(params);
        if (statusFilter) next.set("status", statusFilter);
        else next.delete("status");
        setParams(next, { replace: true });
        // eslint-disable-next-line
    }, [statusFilter]);

    const counts = useMemo(() => {
        const c = { all: list?.length || 0 };
        JOB_STATUSES.forEach((s) => (c[s] = 0));
        (list || []).forEach((l) => (c[l.status] = (c[l.status] || 0) + 1));
        return c;
    }, [list]);

    return (
        <div className="page-shell">
            <header className="fade-up">
                <p className="eyebrow">Jobs</p>
                <h1 className="h1 mt-3" data-testid="jobs-title">
                    Active jobs
                </h1>
                <p className="text-muted mt-2 max-w-[640px]">
                    Track schedule, crew, expenses, and change orders. Convert to an invoice
                    when the work is done.
                </p>
            </header>

            <div className="mt-6 flex items-center gap-2 flex-wrap">
                <button
                    type="button"
                    className={`btn ${statusFilter === "" ? "btn-primary" : "btn-ghost"}`}
                    onClick={() => setStatusFilter("")}
                    data-testid="job-filter-all"
                >
                    All ({counts.all})
                </button>
                {JOB_STATUSES.map((s) => (
                    <button
                        key={s}
                        type="button"
                        className={`btn ${statusFilter === s ? "btn-primary" : "btn-ghost"}`}
                        onClick={() => setStatusFilter(s)}
                        data-testid={`job-filter-${s}`}
                    >
                        <span className="capitalize">{s.replace("_", " ")}</span>
                        <span className="text-xs opacity-70">({counts[s] || 0})</span>
                    </button>
                ))}
            </div>

            <form
                className="mt-4 flex gap-2 flex-wrap"
                onSubmit={(e) => {
                    e.preventDefault();
                    fetchList();
                }}
            >
                <div className="relative flex-1 min-w-[240px]">
                    <Search
                        size={16}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--brand-muted)]"
                    />
                    <input
                        type="text"
                        placeholder="Search by client, address, or job #"
                        className="field-input"
                        style={{ paddingLeft: "2.25rem" }}
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        data-testid="job-search-input"
                    />
                </div>
                <button type="submit" className="btn btn-secondary" data-testid="job-search-btn">
                    Search
                </button>
            </form>

            {err && (
                <div
                    className="brand-card mt-6"
                    style={{ borderColor: "var(--brand-red)", color: "var(--brand-red)" }}
                    data-testid="jobs-error"
                >
                    {err}
                </div>
            )}

            <div
                className="brand-card mt-6 p-0 overflow-hidden fade-up"
                data-testid="jobs-card"
            >
                {list === null ? (
                    <div className="p-8 text-muted">Loading jobs…</div>
                ) : list.length === 0 ? (
                    <div className="p-10 text-center">
                        <Briefcase
                            size={28}
                            style={{ color: "var(--brand-muted)" }}
                            className="mx-auto"
                        />
                        <p className="text-muted mt-3" data-testid="jobs-empty">
                            No jobs yet. Accept an estimate to spin one up.
                        </p>
                        <Link to="/estimates" className="link-red text-sm mt-2 inline-block">
                            Go to estimates
                        </Link>
                    </div>
                ) : (
                    <>
                        <div className="sm:hidden p-3" data-testid="jobs-cards">
                            {list.map((j) => (
                                <MobileListCard
                                    key={j.id}
                                    to={`/jobs/${j.id}`}
                                    testId={`job-card-${j.id}`}
                                    title={j.job_number}
                                    subtitle={j.client?.name}
                                    amount={money(j.estimate_grand_total)}
                                    right={<StatusBadge status={j.status} />}
                                    meta={[
                                        { label: "Address", value: j.job_address },
                                        {
                                            label: "Scheduled",
                                            value: j.scheduled_start
                                                ? fmtDate(j.scheduled_start)
                                                : "Not scheduled",
                                        },
                                        { label: "Estimate", value: j.estimate_number },
                                    ]}
                                />
                            ))}
                        </div>
                        <div className="hidden sm:block overflow-x-auto">
                            <table className="brand-table" data-testid="jobs-table">
                            <thead>
                                <tr>
                                    <th>Number</th>
                                    <th>Client</th>
                                    <th>Address</th>
                                    <th>Scheduled</th>
                                    <th className="text-right">Estimate</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {list.map((j) => (
                                    <tr
                                        key={j.id}
                                        onClick={() => navigate(`/jobs/${j.id}`)}
                                        data-testid={`job-row-${j.id}`}
                                    >
                                        <td>
                                            <p className="font-display font-bold">
                                                {j.job_number}
                                            </p>
                                            <p className="text-xs text-muted">
                                                from {j.estimate_number || "—"}
                                            </p>
                                        </td>
                                        <td>{j.client?.name}</td>
                                        <td className="text-sm">{j.job_address}</td>
                                        <td className="text-sm text-muted">
                                            {j.scheduled_start
                                                ? fmtDate(j.scheduled_start)
                                                : "Not scheduled"}
                                        </td>
                                        <td className="text-right font-mono">
                                            {money(j.estimate_grand_total)}
                                        </td>
                                        <td>
                                            <StatusBadge status={j.status} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
